import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';

import { RadarChartDataPoint } from '@/components/common/RadarChart';
import {
  getHauserEQResultsForProperty,
  HauserEQCategoryScores,
  HauserEQResults,
  isHauserEQConfigured,
} from '@/services/api/hauser-eq/api';
import { logger } from '@/utils/logger/loggerInstance';

/**
 * Format a snake_case or camelCase category key into a display label.
 * e.g., "future_growth" -> "Future Growth", "hvac" -> "HVAC"
 */
const CATEGORY_LABEL_OVERRIDES: Record<string, string> = {
  hvac: 'HVAC & Systems',
  hvac_systems: 'HVAC & Systems',
  natural_hazards: 'Hazards',
  health_quality: 'Health',
};

const formatCategoryLabel = (key: string): string => {
  const override = CATEGORY_LABEL_OVERRIDES[key.toLowerCase()];
  if (override) return override;

  // Convert snake_case to spaces, then title case
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Transform HauserEQ category scores object to RadarChart data format.
 */
export const transformToRadarData = (
  scores: HauserEQCategoryScores | undefined,
): RadarChartDataPoint[] | null => {
  if (!scores) return null;

  return Object.entries(scores)
    .filter((entry): entry is [string, number] => entry[1] !== null)
    .map(([key, value]) => ({
      label: formatCategoryLabel(key),
      value: Math.round(value),
    }));
};

/**
 * Convert a snake_case key to PascalCase.
 * e.g., "home_health" -> "HomeHealth", "risk_exposure" -> "RiskExposure"
 * Keys already in PascalCase pass through unchanged.
 */
const toPascalCase = (key: string): string =>
  key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');

/**
 * Transform HauserEQ subscores to tab format for score cards.
 */
export const transformToScoreTabs = (
  subscores: HauserEQCategoryScores | undefined,
): Array<{ id: string; label: string; score: number }> | null => {
  if (!subscores) return null;

  return Object.entries(subscores)
    .filter((entry): entry is [string, number] => entry[1] !== null)
    .map(([key, value]) => ({
      id: toPascalCase(key),
      label: formatCategoryLabel(key),
      score: Math.round(value),
    }));
};

export const useHauserEQResults = (propertyId: string | undefined) => {
  const isConfigured = isHauserEQConfigured();
  const isEnabled = Boolean(propertyId) && isConfigured;

  // Tracks when we first started polling in the no_list_price+hauserEqKnowsPrice window
  // so we can cap polling at 3 minutes and avoid indefinite network traffic.
  const noListPricePollStartRef = useRef<number | null>(null);

  logger.debug('[useHauserEQResults] Hook called', {
    propertyId,
    isConfigured,
    isEnabled,
  });

  const { data, error, isLoading, isFetching } = useQuery<HauserEQResults>({
    queryKey: ['hauser-eq-results', propertyId],
    queryFn: () => getHauserEQResultsForProperty(propertyId as string),
    enabled: isEnabled,
    // Poll every 5 s while the evaluation is in a transient state so the UI
    // updates automatically once results land without a manual refresh.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const conditionStatus = query.state.data?.condition_status;
      const contextStatus = query.state.data?.context_status;
      const hauserEqKnowsPrice = !!query.state.data?.property_details?.price;
      const contextScoredAt = query.state.data?.context_calculation_timestamp;
      const contextWasJustScored = contextScoredAt
        ? Date.now() - new Date(contextScoredAt).getTime() < 3 * 60 * 1000
        : false;

      if (
        status === 'processing' ||
        status === 'queued' ||
        status === 'partial' ||
        conditionStatus === 'pending' ||
        conditionStatus === 'retrying' ||
        contextStatus === 'retrying'
      ) {
        return 5000;
      }

      // Context narratives are generated asynchronously after the score is
      // saved. Keep polling briefly after a fresh Area rescore so the single
      // batched LLM response appears without requiring a manual refresh.
      if (
        status === 'completed' &&
        contextWasJustScored &&
        !query.state.data?.context_score_subfactor_explanations
      ) {
        return 5000;
      }

      // Also poll when hauser-eq has a price on record but condition_status is still
      // no_list_price — covers the window after a manual price save triggers a recalculate
      // but hauser-eq hasn't transitioned to 'pending' yet. Cap at 3 minutes so we don't
      // poll indefinitely if the backend never transitions out of no_list_price.
      if (conditionStatus === 'no_list_price' && hauserEqKnowsPrice) {
        if (noListPricePollStartRef.current === null) {
          noListPricePollStartRef.current = Date.now();
        }
        const elapsed = Date.now() - noListPricePollStartRef.current;
        const THREE_MINUTES_MS = 3 * 60 * 1000;
        return elapsed < THREE_MINUTES_MS ? 5000 : false;
      }
      // Reset the cap timer when we leave this state
      noListPricePollStartRef.current = null;

      return false;
    },
  });

  // Transform data for easy consumption by components
  const conditionRadarData = transformToRadarData(data?.condition_category_scores);
  const contextRadarData = transformToRadarData(data?.context_category_scores);
  const conditionTabs = transformToScoreTabs(data?.condition_subscores);
  const contextTabs = transformToScoreTabs(data?.context_subscores);

  return {
    // Raw data
    data,
    error,

    // Loading states
    isLoading,
    isFetching,

    // Status helpers
    isCompleted: data?.status === 'completed',
    // Whole-property failure — the Set-Failed-Status safety net for
    // genuinely unexpected errors (e.g. propertylens-api-data itself
    // failing), not a single scoring pipeline. See #17. For a single
    // pipeline's permanent failure, see isContextFailed/isConditionFailed.
    isFailed: data?.status === 'failed',
    isPartial: data?.status === 'partial',
    isContextRetrying: data?.context_status === 'retrying',
    isConditionRetrying: data?.condition_status === 'retrying',
    isContextFailed: data?.context_status === 'failed',
    isConditionFailed: data?.condition_status === 'failed',
    isNoListPrice: data?.condition_status === 'no_list_price',
    isProcessing: data?.status === 'processing' || data?.status === 'queued',
    isConditionProcessing:
      data?.status === 'processing' ||
      data?.status === 'queued' ||
      data?.condition_status === 'pending',

    // Overall scores
    conditionScore: data?.condition_subscores?.home_health ?? data?.condition_score ?? null,
    contextScore: data?.context_score ?? null,

    // Transformed radar chart data
    conditionRadarData,
    contextRadarData,

    // Transformed tab data
    conditionTabs,
    contextTabs,

    // Condition KPIs for inspection summary
    conditionKPIs: data?.condition_kpis ?? null,
    propertyDetails: data?.property_details ?? null,

    // LLM-generated explanations
    contextScoreExplanation: data?.context_score_explanation ?? null,
    conditionScoreExplanation: data?.condition_score_explanation ?? null,
    contextSubscoreExplanations: data?.context_score_subscore_explanations ?? null,
    conditionSubscoreExplanations: data?.condition_score_subscore_explanations ?? null,
    contextCategoryExplanations: data?.context_score_category_explanations ?? null,
    contextSubfactorExplanations: data?.context_score_subfactor_explanations ?? null,
    conditionCategoryExplanations: data?.condition_score_category_explanations ?? null,

    // Score drivers
    conditionScoreDrivers: data?.condition_score_score_drivers ?? null,
    contextScoreDrivers: data?.context_score_score_drivers ?? null,

    // Nearest points of interest
    nearestPointsOfInterest: data?.nearest_points_of_interest ?? null,

    // Building permit history
    permits: data?.permits ?? null,

    // Timestamp of when each score last actually recalculated — a new
    // inspection report only re-runs condition scoring, so these can differ.
    // Falls back to `updated_at` for records computed before this field existed.
    conditionScoredAt: data?.condition_calculation_timestamp ?? data?.updated_at ?? null,
    contextScoredAt: data?.context_calculation_timestamp ?? data?.updated_at ?? null,

    // Failure detail — populated once a pipeline's auto-retry is exhausted
    // for good (see hauser-eq #16/#20). Not a fixed schema; render defensively.
    contextError: data?.context_error ?? null,
    conditionError: data?.condition_error ?? null,
    contextFailedAt: data?.context_failed_at ?? null,
    conditionFailedAt: data?.condition_failed_at ?? null,
  };
};
